/**
 * desktopRunCardHtml.ts — the ONE builder for the "show me on the desktop
 * page" card a computer run posts beside Prest0n's reply (P6b, 2026-09-20).
 *
 * THE INCIDENT. "GitHub is open on your desktop, and I clicked Sign in." It
 * was — on a desktop session the person was not looking at. The run's session
 * id reached Firestore in the tool result's metadata and stopped: nothing on
 * the client read it, the reply described a screen state, and the person had
 * every reason to think honest machinery was lying. The rule this installs:
 * never tell someone to look at a screen without putting them on the right
 * screen. So the run's result carries a typed `desktop_run` signal, and this
 * card turns it into a control that lands on THAT session.
 *
 * WHY IT IS ITS OWN MODULE. The card is DATA (`payload.data`); its html is a
 * projection, rebuilt from the persisted payload at every hydrate seam when the
 * byte-budget backstop blanks the html (connectTileRehydrate → useGlobalChat).
 * That rebuild runs INSIDE the chat reducer, so this file imports the pure card
 * kit and nothing that touches `firebase/auth` at import time — the load-time
 * trap CLAUDE.md names, and the rule authorizeCardHtml.ts records.
 *
 * D9: every string here is STATE copy — where the run finished or stopped, and
 * the control that shows it — never a sentence in Prest0n's voice. The reply
 * itself is the model's.
 */

import { P0AC, escapeHtml, renderCardShell } from './actionCardKit';

/** payload.kind for the card's data-only message. */
export const DESKTOP_RUN_CARD_KIND = 'prest0n.desktop_run';

/** sourceTag on the posted message (D9 inventory: config/code-authored-copy.d). */
export const DESKTOP_RUN_CARD_SOURCE_TAG = 'desktop_run_card';

/** The browser-service's own session-id charset (server.ts mints
 *  `desktop-<ms>-<base36>`; the warm slot is `desktop-warm-…`). */
const DESKTOP_SESSION_ID_RE = /^desktop-[a-z0-9-]{6,64}$/i;

/** One of OUR workstation origins, optionally with its /browser-api path —
 *  the exact rule AgentActivity enforces on a `svc=` arrival. */
const WORKSTATION_SVC_RE = /^https:\/\/(?:\d{1,3}-){3}\d{1,3}\.sslip\.io(?:\/browser-api)?$/i;

export type DesktopRunLang = 'en' | 'es';

export interface DesktopRunCardData {
  sessionId: string;
  app: string;
  /** 'completed' | 'stuck' | … — the run's own outcome word. */
  outcome: string;
  /** The member workstation the run used, when not the account's main one. */
  svc?: string;
  lang: DesktopRunLang;
}

// copy: desktop_run_card
export const DESKTOP_RUN_CARD_COPY = {
  finished: { en: 'finished in {app} on your computer', es: 'terminado en {app} en tu computadora' },
  stopped: { en: 'stopped in {app} on your computer', es: 'se detuvo en {app} en tu computadora' },
  showMe: { en: 'show me on the desktop page', es: 'muéstramelo en la página del escritorio' },
  takeOver: { en: 'take over on the desktop page', es: 'tomar el control en la página del escritorio' },
} as const;

/** The card's in-app href — or null when the data cannot name a session the
 *  desktop page would accept. Mirrors INTERNAL_DESKTOP_ANCHOR_HREF_RE in the
 *  chat sanitizer: a href built here is admitted there. */
export function desktopRunHref(data: { sessionId?: unknown; svc?: unknown }): string | null {
  const id = typeof data.sessionId === 'string' ? data.sessionId.trim() : '';
  if (!DESKTOP_SESSION_ID_RE.test(id)) return null;
  const svc = typeof data.svc === 'string' ? data.svc.trim().replace(/\/+$/, '') : '';
  const svcPart = svc && WORKSTATION_SVC_RE.test(svc) ? `&svc=${encodeURIComponent(svc)}` : '';
  return `/desktop?session=${encodeURIComponent(id)}${svcPart}`;
}

/** Parse a persisted payload's data into card data — null when it cannot
 *  name a session (a payload with no session rebuilds NOTHING, never a
 *  lookalike). */
export function desktopRunCardDataOf(payload: { kind?: string; data?: Record<string, unknown> } | null | undefined): DesktopRunCardData | null {
  if (!payload || payload.kind !== DESKTOP_RUN_CARD_KIND) return null;
  const d = payload.data && typeof payload.data === 'object' ? payload.data : {};
  const sessionId = typeof d.sessionId === 'string' ? d.sessionId.trim() : '';
  if (!DESKTOP_SESSION_ID_RE.test(sessionId)) return null;
  const app = typeof d.app === 'string' && d.app.trim() ? d.app.trim().slice(0, 80) : 'the browser';
  const outcome = typeof d.outcome === 'string' && d.outcome.trim() ? d.outcome.trim().slice(0, 40) : 'unknown';
  const svc = typeof d.svc === 'string' && WORKSTATION_SVC_RE.test(d.svc.trim().replace(/\/+$/, ''))
    ? d.svc.trim().replace(/\/+$/, '')
    : undefined;
  const lang: DesktopRunLang = d.lang === 'es' ? 'es' : 'en';
  return { sessionId, app, outcome, ...(svc ? { svc } : {}), lang };
}

/** The card. Empty string when the data cannot name a session. */
export function buildDesktopRunCardHtml(data: DesktopRunCardData): string {
  const href = desktopRunHref(data);
  if (!href) return '';
  const done = data.outcome === 'completed';
  const lang = data.lang === 'es' ? 'es' : 'en';
  const title = (done ? DESKTOP_RUN_CARD_COPY.finished : DESKTOP_RUN_CARD_COPY.stopped)[lang]
    .replace('{app}', data.app);
  const label = (done ? DESKTOP_RUN_CARD_COPY.showMe : DESKTOP_RUN_CARD_COPY.takeOver)[lang];
  // An <a>, never a <button>: the delegated chat click handler dispatches only
  // buttons, and shouldNavigateInApp turns this internal href into a
  // react-router navigation (same tab — INTERNAL_DESKTOP_ANCHOR_HREF_RE).
  const pill =
    `<a class="p0ac-btn p0ac-btn--md p0ac-btn--primary" href="${escapeHtml(href)}" ` +
    `data-desktop-session="${escapeHtml(data.sessionId)}">${escapeHtml(label)}</a>`;
  return renderCardShell({
    rootAttrs: `data-desktop-run="${escapeHtml(data.sessionId)}"`,
    maxWidth: 560,
    title,
    titleInk: done ? P0AC.INK : P0AC.WARN_INK,
    bodyHtml: `<div class="p0ac-actions">${pill}</div>`,
  });
}
